/**
 * useEvents — hook for the events module (camps, workshops).
 *
 * Scopes listeners per the CLAUDE.md scalability rules:
 *  - Event list: live `onSnapshot` on `gyms/{gymId}/events` (small collection per gym)
 *  - Current user's own participant doc: one single-doc listener per active event
 *  - Current user's own waitlist doc: one single-doc listener per active event
 *  - Admin detail listeners (full participants + waitlist): only when `activeEventId` is set
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  collection, doc, setDoc, deleteDoc, updateDoc, onSnapshot,
  serverTimestamp, query, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useGym } from "../contexts/GymContext";
import { buildEventDraft } from "@sendly/shared";
import { trackMAU } from "./useMAU";
import { logEvent, EVT } from "../analytics";
import { fetchParticipantContact, fetchParticipantContacts } from "../utils/eventContacts";
import { callableErrorMessage } from "../utils/callableError";
import { callableRef } from "../utils/callFunction";
import { eventStatusPatch } from "../utils/eventLifecycle";

const registerForEventCF = callableRef("registerForEvent");
const deleteEventCascadeCF = callableRef("deleteEventCascade");
const updateEventParticipantCF = callableRef("updateEventParticipant");
const setEventAttendanceCF = callableRef("setEventAttendance");
const promoteFromWaitlistCF = callableRef("promoteFromWaitlist");

/**
 * Entries per setEventAttendance call — mirrors MAX_ATTENDANCE_ENTRIES in
 * apps/functions/src/events.js. "Mark everybody" on a camp bigger than this
 * goes out in several calls rather than being refused.
 */
const ATTENDANCE_CALL_CHUNK = 500;

/**
 * @param {object|null} authUser
 * @param {boolean|undefined} isMember Gym membership, tri-state: `undefined`
 *   while the own-member listener hasn't answered yet, then true/false. Every
 *   listener below is gated on it because the rule is
 *   `allow read: if request.auth != null && isActiveGymMember(gymId)` — see the
 *   comment on the event-list effect for what attaching too early cost.
 */
export function useEvents(authUser, isMember) {
  const { gymId, gymMeta } = useGym();

  // Top-level event list (all statuses)
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Per-event detail (only loaded when activeEventId is set — i.e. admin editor open)
  const [activeEventId, setActiveEventId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [waitlist, setWaitlist] = useState([]);

  // Per-user registration map: { eventId: participantDoc | null }
  const [myParticipantMap, setMyParticipantMap] = useState({});
  // Per-user waitlist map: { eventId: waitlistDoc | null }
  const [myWaitlistMap, setMyWaitlistMap] = useState({});
  // Proxy children per event (confirmed participants): { eventId: [participantDoc, ...] }
  const [myProxyChildren, setMyProxyChildren] = useState({});
  // Proxy children on the WAITLIST per event: { eventId: [waitlistDoc, ...] }
  const [myProxyWaitlist, setMyProxyWaitlist] = useState({});

  // ── Event list listener ──
  //
  // Gated on gym MEMBERSHIP, not just on gymId. The rule is
  // `allow read: if request.auth != null && isActiveGymMember(gymId)`, so attaching
  // any earlier is a listen the rules reject — the same "never attempt a listen
  // the rules would deny" gate memberContacts already has in useGymData.
  //
  // Two ways it used to attach too early. A signed-out visitor: these hooks run
  // BEFORE MainApp's sign-in gate (useMainApp is called at the top, the
  // `!authUser` return is ~200 lines below), and this effect only waited for
  // `gymId`, which resolves without auth. Wasteful but invisible.
  //
  // The one that actually cost a user something is the FIRST entry into a gym:
  // useAuth creates the member doc asynchronously (it reads the members
  // collection, then writes), while this listener fired the moment `gymId` was
  // known. A permission-denied listener does NOT retry, and the effect depended
  // on `gymId` alone — so nothing reattached it when the member doc landed, and
  // a brand-new member's Események tab stayed empty until they reloaded. First
  // impression, on the one visit where it matters most.
  //
  // `isMember` comes from GymContext's server-confirmed `memberState` (via
  // useMainApp), so it flips once the SERVER has the member doc — never on
  // useAuth's own pending write — and this resubscribes on its own.
  useEffect(() => {
    if (!gymId || isMember !== true) {
      setEvents([]);
      // Membership still resolving → stay loading rather than flash an empty
      // list; a definitive "not a member" ends the loading state.
      setLoading(!!gymId && isMember === undefined);
      return;
    }
    const col = collection(db, "gyms", gymId, "events");
    let initialized = false;
    const unsub = onSnapshot(col, snap => {
      if (initialized && snap.docChanges().length === 0) return;
      initialized = true;
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => {
      setLoading(false);
    });
    return unsub;
  }, [gymId, isMember]);

  // Stable join of active event ids (status published) to avoid re-running the per-user listeners on every list change
  const activeEventIds = events
    .filter(e => e.status === "published")
    .map(e => e.id)
    .sort()
    .join(",");

  // ── Per-user participant listeners ──
  //
  // Both maps PLANT the key with `null` when there is no doc, so `id in map`
  // means "this listener has answered" — the same readiness signal the
  // competition `myRegistrationMap` gives. The `?calFocus=event:…&reg=` intent
  // from the public landing waits on it: acting before the answer would open
  // a blank "new sign-up" form for someone who is already signed up.
  useEffect(() => {
    if (!gymId || !authUser?.uid || isMember !== true || !activeEventIds) {
      setMyParticipantMap({});
      return;
    }
    const ids = activeEventIds.split(",").filter(Boolean);
    if (ids.length === 0) { setMyParticipantMap({}); return; }

    const unsubs = ids.map(eventId => {
      const ref = doc(db, "gyms", gymId, "events", eventId, "participants", authUser.uid);
      return onSnapshot(ref, snap => {
        setMyParticipantMap(prev => {
          const val = snap.exists() ? { id: snap.id, ...snap.data() } : null;
          if (val === null && eventId in prev && prev[eventId] === null) return prev;
          return { ...prev, [eventId]: val };
        });
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, [gymId, authUser?.uid, isMember, activeEventIds]);

  // ── Per-user waitlist listeners ──
  useEffect(() => {
    if (!gymId || !authUser?.uid || isMember !== true || !activeEventIds) {
      setMyWaitlistMap({});
      return;
    }
    const ids = activeEventIds.split(",").filter(Boolean);
    if (ids.length === 0) { setMyWaitlistMap({}); return; }

    const unsubs = ids.map(eventId => {
      const ref = doc(db, "gyms", gymId, "events", eventId, "waitlist", authUser.uid);
      return onSnapshot(ref, snap => {
        setMyWaitlistMap(prev => {
          const val = snap.exists() ? { id: snap.id, ...snap.data() } : null;
          if (val === null && eventId in prev && prev[eventId] === null) return prev;
          return { ...prev, [eventId]: val };
        });
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, [gymId, authUser?.uid, isMember, activeEventIds]);

  // ── Admin detail listener (full participants + waitlist) ──
  useEffect(() => {
    if (!gymId || !activeEventId || isMember !== true) {
      setParticipants([]);
      setWaitlist([]);
      return;
    }
    const base = `gyms/${gymId}/events/${activeEventId}`;

    const u1 = onSnapshot(collection(db, base, "participants"), snap => {
      setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
    const u2 = onSnapshot(collection(db, base, "waitlist"), snap => {
      setWaitlist(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
    return () => { u1(); u2(); };
  }, [gymId, activeEventId, isMember]);

  // Per-user proxy children listeners — one query per published event for
  // the current user's proxy children. Scoped to `proxyUid == me`, so it
  // returns only the children the parent actually registered (1–5 docs per
  // event in practice, bounded by the event's proxyLimit).
  useEffect(() => {
    if (!gymId || !authUser?.uid || isMember !== true || !activeEventIds) {
      setMyProxyChildren({});
      return;
    }
    const ids = activeEventIds.split(",").filter(Boolean);
    if (ids.length === 0) { setMyProxyChildren({}); return; }

    const unsubs = ids.map(eventId => {
      const q = query(
        collection(db, "gyms", gymId, "events", eventId, "participants"),
        where("proxyUid", "==", authUser.uid)
      );
      return onSnapshot(q, snap => {
        setMyProxyChildren(prev => {
          const children = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          const old = prev[eventId];
          if (!old && children.length === 0) return prev;
          return { ...prev, [eventId]: children };
        });
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, [gymId, authUser?.uid, isMember, activeEventIds]);

  // Per-user proxy children on the WAITLIST — same pattern but queries
  // the waitlist subcollection instead of participants.
  useEffect(() => {
    if (!gymId || !authUser?.uid || isMember !== true || !activeEventIds) {
      setMyProxyWaitlist({});
      return;
    }
    const ids = activeEventIds.split(",").filter(Boolean);
    if (ids.length === 0) { setMyProxyWaitlist({}); return; }

    const unsubs = ids.map(eventId => {
      const q = query(
        collection(db, "gyms", gymId, "events", eventId, "waitlist"),
        where("proxyUid", "==", authUser.uid)
      );
      return onSnapshot(q, snap => {
        setMyProxyWaitlist(prev => {
          const entries = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(e => e.status === "waiting");
          const old = prev[eventId];
          if (!old && entries.length === 0) return prev;
          return { ...prev, [eventId]: entries };
        });
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, [gymId, authUser?.uid, isMember, activeEventIds]);

  // ── Actor stamping ──
  //
  // Same contract as the competition hook: every event-scoped write records the
  // acting user in `lastModifiedBy`, the Firestore rules pin it to
  // `request.auth.uid`, and the audit triggers read it because a trigger has no
  // caller identity of its own. The uid lives in a ref so the helper needs no
  // dependencies and cannot go stale in a callback that forgot to list it.
  const actorUidRef = useRef(authUser?.uid || null);
  actorUidRef.current = authUser?.uid || null;

  /** Stamp a write payload with the acting user. `bulkOpId`: see useCompetition. */
  const withActor = useCallback((data, bulkOpId) => ({
    ...data,
    lastModifiedBy: actorUidRef.current,
    ...(bulkOpId ? { _auditBulk: bulkOpId } : {}),
  }), []);

  /** Event-doc write with the actor stamp — four status transitions share it. */
  const writeEvent = useCallback((eventId, data) => updateDoc(
    doc(db, "gyms", gymId, "events", eventId),
    withActor(data),
  ), [gymId, withActor]);

  /**
   * Delete a doc while keeping the audit trail honest — a Firestore delete
   * carries no payload, so without this the entry would name whoever last
   * edited the doc instead of whoever removed it. See useCompetition.
   */
  const deleteAttributed = useCallback(async (ref) => {
    await updateDoc(ref, { lastModifiedBy: actorUidRef.current }).catch(() => {});
    await deleteDoc(ref);
  }, []);

  // ── Event CRUD ──

  const createEvent = useCallback(async (data) => {
    if (!gymId || !authUser) return null;
    const eventId = "evt_" + Date.now();
    // The skeleton and the forced draft status are the shared builder's — the
    // MCP server creates event drafts with the same one. `country` decides
    // the default language the two descriptions mirror (gymDefaultLang).
    const doc_ = {
      ...buildEventDraft(data, { gymId, country: gymMeta?.country }),
      createdBy: authUser.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, "gyms", gymId, "events", eventId), withActor(doc_));
    return eventId;
  }, [gymId, gymMeta, authUser, withActor]);

  const updateEvent = useCallback(async (eventId, data) => {
    if (!gymId || !eventId) return;
    await writeEvent(eventId, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }, [gymId]);

  /**
   * Server-side cascade, same reasoning as the competition one: a full camp's
   * `participants` collection can pass Firestore's 500-op batch cap, and the
   * audit triggers need the parent doc gone first so they don't log into an
   * event that is being deleted. See functions/src/cascadeDelete.js.
   */
  const deleteEvent = useCallback(async (eventId) => {
    if (!gymId || !eventId) return;
    await deleteEventCascadeCF({ gymId, eventId });
  }, [gymId]);

  // ── Publish / status transitions ──

  const publishEvent = useCallback(async (eventId) => {
    if (!gymId || !eventId) return;
    await writeEvent(eventId, eventStatusPatch("published", { timestamp: serverTimestamp() }));
  }, [gymId]);

  const closeEvent = useCallback(async (eventId) => {
    if (!gymId || !eventId) return;
    await writeEvent(eventId, eventStatusPatch("closed", { timestamp: serverTimestamp() }));
  }, [gymId]);

  const cancelEvent = useCallback(async (eventId) => {
    if (!gymId || !eventId) return;
    await writeEvent(eventId, eventStatusPatch("cancelled", { timestamp: serverTimestamp() }));
  }, [gymId]);

  // ── Registration (callable CF) ──

  const registerForEvent = useCallback(async (eventId, data) => {
    if (!gymId || !authUser) return null;
    try {
      const result = await registerForEventCF({
        gymId,
        eventId,
        uid: authUser.uid,
        displayName: data.displayName || "",
        groupId: data.groupId || null,
        isGuest: false,
        proxyUid: null,
        birthYear: data.birthYear || null,
        allergies: data.allergies || "",
        medicalNotes: data.medicalNotes || "",
        parentName: data.parentName || "",
        parentPhone: data.parentPhone || "",
        parentEmail: data.parentEmail || "",
        parentalConsent: !!data.parentalConsent,
      });
      // Fire-and-forget: the surrounding try/catch does NOT cover an un-awaited
      // promise, so without this .catch a rules rejection (non-member uid)
      // escapes as an unhandled rejection into the crash log.
      trackMAU(gymId, authUser.uid).catch(() => {});
      // `early_bird` comes from the server's answer (the snapshot it wrote), not
      // from the client clock: did this sign-up actually get a discount.
      logEvent(EVT.EVENT_REGISTER, { gym_id: gymId, is_proxy: false, early_bird: result?.data?.earlyBird?.discountPct > 0 });
      return result?.data || {};
    } catch (err) {
      console.error("registerForEvent CF error:", err);
      throw new Error(callableErrorMessage(err, "Registration failed"));
    }
  }, [gymId, authUser]);

  const registerProxyChild = useCallback(async (eventId, data) => {
    if (!gymId || !authUser) return null;
    try {
      // Generate a guest id on the client for proxy children
      const guestId = `guest_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`;
      const result = await registerForEventCF({
        gymId,
        eventId,
        uid: guestId,
        displayName: data.displayName || "",
        groupId: data.groupId || null,
        isGuest: true,
        proxyUid: authUser.uid,
        birthYear: data.birthYear || null,
        allergies: data.allergies || "",
        medicalNotes: data.medicalNotes || "",
        parentName: data.parentName || "",
        parentPhone: data.parentPhone || "",
        parentEmail: data.parentEmail || "",
        parentalConsent: !!data.parentalConsent,
      });
      logEvent(EVT.EVENT_REGISTER, { gym_id: gymId, is_proxy: true, early_bird: result?.data?.earlyBird?.discountPct > 0 });
      return result?.data || { participantId: guestId };
    } catch (err) {
      console.error("registerProxyChild CF error:", err);
      throw new Error(callableErrorMessage(err, "Proxy registration failed"));
    }
  }, [gymId, authUser]);

  const registerAdminGuest = useCallback(async (eventId, data) => {
    if (!gymId || !authUser) return null;
    try {
      const guestId = `guest_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`;
      const result = await registerForEventCF({
        gymId,
        eventId,
        uid: guestId,
        displayName: data.displayName || "",
        groupId: data.groupId || null,
        isGuest: true,
        proxyUid: null,
        birthYear: data.birthYear || null,
        allergies: data.allergies || "",
        medicalNotes: data.medicalNotes || "",
        parentName: data.parentName || "",
        parentPhone: data.parentPhone || "",
        parentEmail: data.parentEmail || "",
        parentalConsent: !!data.parentalConsent,
      });
      return result?.data || { participantId: guestId };
    } catch (err) {
      console.error("registerAdminGuest CF error:", err);
      throw new Error(callableErrorMessage(err, "Admin guest registration failed"));
    }
  }, [gymId, authUser]);

  const registerAdminUser = useCallback(async (eventId, memberUid, data) => {
    if (!gymId || !authUser) return null;
    try {
      const result = await registerForEventCF({
        gymId,
        eventId,
        uid: memberUid,
        displayName: data.displayName || "",
        groupId: data.groupId || null,
        isGuest: false,
        proxyUid: null,
        birthYear: data.birthYear || null,
        allergies: data.allergies || "",
        medicalNotes: data.medicalNotes || "",
        parentName: data.parentName || "",
        parentPhone: data.parentPhone || "",
        parentEmail: data.parentEmail || "",
        parentalConsent: !!data.parentalConsent,
      });
      return result?.data || {};
    } catch (err) {
      console.error("registerAdminUser CF error:", err);
      throw new Error(callableErrorMessage(err, "Admin user registration failed"));
    }
  }, [gymId, authUser]);

  /** Cancel your own participation (or admin-initiated cancel). */
  const cancelParticipation = useCallback(async (eventId, participantId, { hardDelete = false } = {}) => {
    if (!gymId || !eventId || !participantId) return;
    const ref = doc(db, "gyms", gymId, "events", eventId, "participants", participantId);
    if (hardDelete) {
      await deleteAttributed(ref);
    } else {
      await updateDoc(ref, withActor({
        status: "cancelled",
        cancelledAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }));
    }
    logEvent(EVT.EVENT_UNREGISTER, { gym_id: gymId });
  }, [gymId, withActor, deleteAttributed]);

  /**
   * Admin-only field write on a participant (payment amounts, notes).
   * `{ earlyBirdOverride: tierId | "none" | "auto" }` is the one exception: it
   * is not a field write but the staff fee-category override, routed to the
   * `updateEventParticipant` callable (see below) and resolving to the new
   * snapshot (or null).
   * NOT for `groupId` — a group move is capacity-checked, which Firestore
   * rules cannot do, so it goes through `editParticipant` below.
   */
  const updateParticipant = useCallback(async (eventId, participantId, patch) => {
    if (!gymId || !eventId || !participantId) return;
    // The early-bird override is not a stored field the client may write: the
    // snapshot is server-owned, and the callable recomputes the payer's billing
    // from it. It travels alone — mixing it with a plain field write would
    // leave half of the patch applied if the callable then refused.
    if (patch && Object.prototype.hasOwnProperty.call(patch, "earlyBirdOverride")) {
      const { earlyBirdOverride, ...rest } = patch;
      if (Object.keys(rest).length > 0) throw new Error("earlyBirdOverride cannot be combined with other fields");
      try {
        const res = await updateEventParticipantCF({ gymId, eventId, participantId, earlyBirdOverride });
        // No PII: only the kind of change. The participants listener refreshes the panel.
        logEvent(EVT.ADMIN_EARLY_BIRD_OVERRIDE, {
          gym_id: gymId,
          module: "event",
          action: earlyBirdOverride === "none" ? "revoke" : earlyBirdOverride === "auto" ? "auto" : "grant",
        });
        return res?.data?.earlyBird ?? null;
      } catch (err) {
        const code = err?.code || err?.details?.code || "";
        if (!code.includes("failed-precondition") && !code.includes("permission-denied")
            && !code.includes("invalid-argument")) {
          console.error("updateEventParticipant CF error (early-bird override):", err);
        }
        throw new Error(callableErrorMessage(err, "Fee category update failed"));
      }
    }
    await updateDoc(
      doc(db, "gyms", gymId, "events", eventId, "participants", participantId),
      withActor({ ...patch, updatedAt: serverTimestamp() })
    );
  }, [gymId, withActor]);

  /**
   * Move a participant to another group and/or correct their details, through
   * the CF that re-checks capacity and the age cohort — and keeps the
   * denormalized `participantCounts` in step, which the old client-side write
   * never did. `groupId` is denied to clients in the security rules, so this
   * is the only path that changes it (parent editing their child, admin
   * moving someone between groups).
   */
  const editParticipant = useCallback(async (eventId, participantId, patch) => {
    if (!gymId || !eventId || !participantId) return;
    try {
      await updateEventParticipantCF({ gymId, eventId, participantId, ...patch });
    } catch (err) {
      const code = err?.code || err?.details?.code || "";
      if (!code.includes("failed-precondition") && !code.includes("permission-denied")
          && !code.includes("invalid-argument")) {
        console.error("updateEventParticipant CF error:", err);
      }
      throw new Error(callableErrorMessage(err, "Participant update failed"));
    }
  }, [gymId]);

  /**
   * Toggle / set attendance for a day.
   *
   * Goes through the `setEventAttendance` callable, not `updateDoc`: the rules
   * refuse `attendance` to every client now, because that is the only way the
   * two-preset `events.checkIn` capability can actually decide anything (a
   * rules helper cannot resolve a cap that lives in two presets). The callable
   * runs the real resolver and admits `events.checkIn`, `events.manage` or the
   * event's organiser.
   *
   * The trade this makes: the write is no longer optimistic, so it needs a
   * connection and the caller has to show a pending state. See AttendanceTab.
   */
  const setAttendance = useCallback(async (eventId, participantId, date, present) => {
    if (!gymId || !eventId || !participantId || !date) return;
    try {
      const res = await setEventAttendanceCF({
        gymId, eventId, date,
        entries: [{ participantId, present: !!present }],
      });
      logEvent(EVT.EVENT_CHECK_IN, {
        gym_id: gymId, mode: "single", count: res?.data?.updated ?? 1,
      });
    } catch (err) {
      console.error("setEventAttendance CF error:", err);
      throw new Error(callableErrorMessage(err, "Attendance write failed"));
    }
  }, [gymId]);

  /**
   * "Mark everybody present / absent" for a day. Sliced to the callable's
   * per-call entry limit, so a camp larger than one slice still goes through —
   * in several calls, each of which commits on its own.
   */
  const bulkSetAttendance = useCallback(async (eventId, updates, date) => {
    if (!gymId || !eventId || !date || !Array.isArray(updates) || updates.length === 0) return;
    const entries = updates.map(u => ({ participantId: u.participantId, present: !!u.present }));
    try {
      let updated = 0;
      for (let i = 0; i < entries.length; i += ATTENDANCE_CALL_CHUNK) {
        const res = await setEventAttendanceCF({ gymId, eventId, date, entries: entries.slice(i, i + ATTENDANCE_CALL_CHUNK) });
        updated += res?.data?.updated ?? 0;
      }
      logEvent(EVT.EVENT_CHECK_IN, { gym_id: gymId, mode: "bulk", count: updated });
    } catch (err) {
      console.error("setEventAttendance CF error (bulk):", err);
      throw new Error(callableErrorMessage(err, "Attendance write failed"));
    }
  }, [gymId]);

  // ── Contact documents (gated `participantContacts`) ──
  //
  // One-shot reads, never listeners: the participant / waitlist documents the
  // listeners above deliver carry no birth year, health or guardian data any
  // more (it is readable by staff, the entrant and the proxy parent only).
  // Whoever needs it — an edit form, an export — reads it on demand and merges
  // it by id. See docs/spec/10c-events-participant-contacts.md.

  /** One entrant's contact doc, or null (missing / not permitted). */
  const loadParticipantContact = useCallback(
    (eventId, entrantId) => fetchParticipantContact(db, gymId, eventId, entrantId),
    [gymId]
  );

  /** Every contact doc of an event as `{ [entrantId]: data }` (staff-only list rule). */
  const loadParticipantContacts = useCallback(
    (eventId) => fetchParticipantContacts(db, gymId, eventId),
    [gymId]
  );

  // ── Waitlist management (admin) ──

  const promoteWaitlistEntry = useCallback(async (eventId, waitlistId) => {
    if (!gymId || !eventId || !waitlistId) return;
    try {
      await promoteFromWaitlistCF({ gymId, eventId, waitlistId });
    } catch (err) {
      console.error("promoteFromWaitlist CF error:", err);
      throw new Error(callableErrorMessage(err, "Promotion failed"));
    }
  }, [gymId]);

  const declineWaitlistEntry = useCallback(async (eventId, waitlistId) => {
    if (!gymId || !eventId || !waitlistId) return;
    await updateDoc(
      doc(db, "gyms", gymId, "events", eventId, "waitlist", waitlistId),
      withActor({ status: "declined" })
    );
  }, [gymId]);

  const leaveWaitlist = useCallback(async (eventId, waitlistId) => {
    if (!gymId || !eventId || !waitlistId) return;
    await deleteAttributed(doc(db, "gyms", gymId, "events", eventId, "waitlist", waitlistId));
  }, [gymId, deleteAttributed]);

  return {
    // Data
    events, loading,
    activeEventId, setActiveEventId,
    participants, waitlist,
    myParticipantMap, myWaitlistMap, myProxyChildren, myProxyWaitlist,

    // CRUD
    createEvent, updateEvent, deleteEvent,
    publishEvent, closeEvent, cancelEvent,

    // Registration
    registerForEvent, registerProxyChild, registerAdminGuest, registerAdminUser,
    cancelParticipation, updateParticipant, editParticipant,

    // Attendance
    setAttendance, bulkSetAttendance,

    // Waitlist
    promoteWaitlistEntry, declineWaitlistEntry, leaveWaitlist,

    // Gated contact documents (one-shot reads)
    loadParticipantContact, loadParticipantContacts,
  };
}
